/* eslint-disable max-lines */
import Paper from '@mui/material/Paper';
import React, { useEffect, useState } from 'react';
import { ColumnDef, ColumnFilter, Filter } from './types';
import { FilterLines, FilterFunnelOne } from '@im/ui-lib/zenith/icons';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DatePickerProvider } from 'src/provider/DatePickerProvider';
import Autocomplete from '@mui/material/Autocomplete';
import { v4 as uuidV4 } from 'uuid';
import Typography from '@mui/material/Typography';
import { useTranslate } from '@im/react-localization';
import { useTheme } from '@mui/system';
import Checkbox from '@mui/material/Checkbox';
import { PresetRangePicker } from '@im/ui-lib/Calendar/PresetRangePicker';
import dayjs from 'dayjs';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';

const DATE_FORMAT = 'YYYY-MM-DD';

const formatDate = (date?: Date | string | null, format?: string) => {
  if (!date) {
    return '';
  }
  return dayjs(date).format(format ?? DATE_FORMAT);
};

const applyStyleToDateRangePicker = () => {
  const datePickerElement = document.querySelector<HTMLElement>(
    '[data-testid="filter-date-range-Preset-Range-Popper"]'
  );

  if (!datePickerElement) {
    return false;
  }

  datePickerElement.style.setProperty('z-index', '1301', 'important');
  return true;
};

type FilterComponentProps<T> = {
  appliedFilterOnCol: Filter;
  column: ColumnDef<T>;
  filterBy: ColumnDef<T>['field'];
  onAutoCompleteValueChange: (value: string) => void;
  onFilterApply?: (filter: ColumnFilter) => void;
  openFilters: Element | null;
  setFilterBy: React.Dispatch<React.SetStateAction<'' | keyof T>>;
  setOpenFilters: React.Dispatch<React.SetStateAction<Element | null>>;
};

const FilterComponent = <T,>(props: FilterComponentProps<T>) => {
  const translate = useTranslate();
  const theme = useTheme();
  const formatDateTime = useDateTimeFormatter();

  const [filter, setFilter] = useState<Filter>({
    action: '',
    value: '',
  });

  const handleSubmit = () => {
    props.setOpenFilters(null);
    props.onFilterApply?.({
      [props.column.field]: {
        action: filter.action,
        type: filter.type,
        value: filter.value,
      },
    });
  };

  useEffect(() => {
    if (props.appliedFilterOnCol) {
      setFilter(props.appliedFilterOnCol);
    } else {
      setFilter({
        action: props.column.filters?.actions?.[0]?.value ?? '',
        value: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.appliedFilterOnCol]);

  useEffect(() => {
    if (
      !(props.column.filters?.type === 'date' && filter.type === 'date-range')
    ) {
      return;
    }

    if (applyStyleToDateRangePicker()) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (applyStyleToDateRangePicker()) {
        return;
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [filter.type, props.column.filters?.type]);

  return (
    <>
      <Stack
        data-testid={`${props.column.field as string}-filter-component`}
        id="filter"
        onClick={(e) => {
          props.setFilterBy(props.column.field);
          props.setOpenFilters(e.currentTarget);
        }}
        sx={{
          '.MuiSvgIcon-root': {
            color: 'rgb(100, 100, 100) !important',
          },
          cursor: 'pointer',
        }}
      >
        {props?.appliedFilterOnCol ? (
          <FilterFunnelOne
            size={18}
            sx={{ opacity: 1, transition: 'opacity 0.1s' }}
          />
        ) : (
          <FilterLines
            size={18}
            sx={{
              '.MuiTableCell-head:hover &': {
                opacity: 0.5,
              },
              opacity:
                props.openFilters && props.filterBy === props.column.field
                  ? 1
                  : 0,
              transition: 'opacity 0.1s',
            }}
          />
        )}
      </Stack>
      {props.column.field === props.filterBy && (
        <Menu
          anchorEl={props.openFilters}
          onClose={() => props.setOpenFilters(null)}
          open={!!props.openFilters}
          sx={{
            '.MuiPaper-root': {
              width: 320,
            },
          }}
        >
          <Stack flexDirection="column" gap={2}>
            <Stack gap={1}>
              <Stack>
                <Typography
                  color={theme.palette.grey[700]}
                  fontWeight={600}
                  variant="bodycopyregular"
                >
                  {translate('Common.Action')}
                </Typography>
                <TextField
                  SelectProps={{
                    MenuProps: {
                      MenuListProps: {
                        sx: { padding: `8px 0px !important` },
                      },
                    },
                  }}
                  data-testid={`${
                    props.filterBy as string
                  }-filter-action-select`}
                  fullWidth
                  required
                  select
                  value={filter?.action}
                >
                  {props.column?.filters?.actions.map((action) => (
                    <MenuItem
                      key={action.value}
                      onClick={() => {
                        setFilter({
                          ...filter,
                          action: action.value,
                          type: action.optionType,
                        });
                      }}
                      sx={{ px: 2 }}
                      value={action.value}
                    >
                      {action.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack>
                <Typography
                  color={theme.palette.grey[700]}
                  fontWeight={600}
                  variant="bodycopyregular"
                >
                  {translate(`Common.Value`)}
                </Typography>
                {props.column?.filters?.type === 'date' && (
                  <>
                    {filter?.type === 'date-range' ? (
                      <PresetRangePicker
                        endDate={dayjs(filter?.value?.[1]).toDate()}
                        format={(sDate, eDate) =>
                          !sDate ||
                          !eDate ||
                          !filter?.value?.[0] ||
                          !filter?.value?.[1]
                            ? translate('Common.DateRangePlaceholder')
                            : `${formatDateTime(sDate)} to ${formatDateTime(
                                eDate
                              )}`
                        }
                        id="filter-date-range"
                        isPreset
                        onClearClick={() =>
                          setFilter((prev) => ({
                            ...prev,
                            value: [],
                          }))
                        }
                        onDateChange={(startDate, endDate) => {
                          setFilter({
                            ...filter,
                            value: [
                              formatDate(
                                startDate,
                                props.column.filters?.dateOptions?.dateFormat
                              ),
                              formatDate(
                                endDate,
                                props.column.filters?.dateOptions?.dateFormat
                              ),
                            ],
                          });
                        }}
                        onIntervalSelected={(dates) => {
                          setFilter({
                            ...filter,
                            value: [
                              formatDate(
                                dates.actualStartDate,
                                props.column.filters?.dateOptions?.dateFormat
                              ),
                              formatDate(
                                dates.actualEndDate,
                                props.column.filters?.dateOptions?.dateFormat
                              ),
                            ],
                          });
                        }}
                        startDate={dayjs(filter?.value?.[0]).toDate()}
                        sx={{
                          '.MuiFormControl-root': {
                            width: 'auto',
                          },
                        }}
                      />
                    ) : (
                      <DatePickerProvider>
                        <DatePicker
                          onChange={(value) =>
                            setFilter({
                              ...filter,
                              value: formatDate(
                                value?.toString(),
                                props.column.filters?.dateOptions?.dateFormat
                              ),
                            })
                          }
                          renderInput={(params) => (
                            <TextField {...params} fullWidth name="value" />
                          )}
                          value={filter?.value || null}
                        />
                      </DatePickerProvider>
                    )}
                  </>
                )}
                {props.column?.filters?.type === 'option' && (
                  <Autocomplete
                    PaperComponent={({ children }) => <Paper>{children}</Paper>}
                    data-testid={`${
                      props.filterBy as string
                    }-filter-value-autocomplete`}
                    disableCloseOnSelect
                    fullWidth
                    multiple
                    onChange={(_, options) =>
                      setFilter({
                        ...filter,
                        value: options.map((o) => o.value),
                      })
                    }
                    onInputChange={(e, v, r) =>
                      props.onAutoCompleteValueChange?.(v)
                    }
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    options={props.column?.filters?.options ?? []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off',
                          autoFocus: true,
                          size: 'small',
                        }}
                      />
                    )}
                    renderOption={(renderProps, option, { selected }) => (
                      <MenuItem
                        {...renderProps}
                        dense
                        key={uuidV4()}
                        value={option.value}
                      >
                        <Checkbox checked={selected} size="small" />
                        {option.label}
                      </MenuItem>
                    )}
                    renderTags={(value) => (
                      <Typography
                        sx={{ color: theme.palette.grey[600] }}
                        variant="bodycopyregular"
                      >
                        {value.map((v) => v.label).join(', ')}
                      </Typography>
                    )}
                    value={props?.column?.filters?.options?.filter((o) =>
                      filter.value.includes(o.value)
                    )}
                  />
                )}
                {(!props.column?.filters?.type ||
                  props.column?.filters?.type === 'text') && (
                  <TextField
                    InputProps={{ autoFocus: true }}
                    data-testid={`${
                      props.filterBy as string
                    }-filter-value-textfield`}
                    fullWidth
                    onChange={(e) => {
                      setFilter({ ...filter, value: e.target.value });
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      e.key === 'Enter' && handleSubmit();
                    }}
                    required
                    value={filter?.value}
                  />
                )}
              </Stack>
              <Stack alignSelf={'flex-end'} flexDirection="row" gap={1}>
                <Button
                  onClick={() => {
                    props.setOpenFilters(null);
                  }}
                  size="small"
                  sx={{ fontSize: '14px', px: 0, py: 0 }}
                  variant="newOutlined"
                >
                  {translate('Common.Cancel')}
                </Button>
                <Button
                  onClick={() => {
                    setFilter({
                      action: props.column.filters?.actions?.[0]?.value ?? '',
                      value: '',
                    });
                    props.onFilterApply?.({
                      [props?.column?.field]: null,
                    });
                  }}
                  size="small"
                  sx={{ fontSize: '14px', px: 0, py: 0 }}
                  variant="newOutlined"
                >
                  {translate('Common.Reset')}
                </Button>

                <Button
                  disabled={!(filter?.action && filter?.value)}
                  onClick={handleSubmit}
                  size="small"
                  sx={{ fontSize: '14px', px: 0, py: 0 }}
                  variant="newContained"
                >
                  {translate('Common.Submit')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Menu>
      )}
    </>
  );
};

export default FilterComponent;
